/**
 * Dual-program calendar — Medicare and Medicaid side by side, with a
 * Month / List switcher.
 *
 * The one question this screen answers at a glance, with zero filtering:
 * WHAT is due WHEN, and WHICH KIND of duty it is. Kind is encoded in color
 * on every chip and row:
 *   emerald = Medicaid required deliverable (the contract's Tables 6 & 7)
 *   amber   = Medicaid triggered duty (a reported event created it)
 *   blue    = Medicare (CMS)
 * Overdue turns red regardless of kind; completed goes quiet.
 *
 * Triggered duties only EXIST on the calendar after an event is reported —
 * so the Medicaid panel also carries the "When it happens" list: every
 * active event-conditioned duty from the contract, its trigger, and the
 * deadline the contract imposes once it fires. Visible before it ever
 * happens, which is the point.
 */
import React, { useEffect, useMemo, useState } from 'react';

const SMAC_TYPE = 'State Medicaid (SMAC)';

export function itemKind(item) {
  const type = item.obligation_type || item.type || '';
  if (type !== SMAC_TYPE) return 'medicare';
  // Scheduled deliverables trace to a dated (Tables 6 & 7) register row;
  // instances spawned by report-an-event trace to conditional/monitorable rows.
  return item.smac_category === 'dated' ? 'deliverable' : 'triggered';
}

const KIND_META = {
  deliverable: { label: 'Required deliverable', dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100' },
  triggered: { label: 'Triggered duty', dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100' },
  medicare: { label: 'Medicare (CMS)', dot: 'bg-blue-500', chip: 'bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100' },
};

const STATUS_PILL = {
  overdue: 'bg-red-100 text-red-700 border-red-200',
  due_soon: 'bg-amber-100 text-amber-800 border-amber-200',
  in_progress: 'bg-sky-100 text-sky-700 border-sky-200',
  completed: 'bg-slate-100 text-slate-500 border-slate-200',
  no_action_needed: 'bg-slate-100 text-slate-500 border-slate-200',
  upcoming: 'bg-blue-50 text-blue-700 border-blue-200',
};
const STATUS_LABEL = {
  overdue: 'Overdue', due_soon: 'Due soon', in_progress: 'In progress',
  completed: 'Completed', no_action_needed: 'No action needed', upcoming: 'Upcoming',
};

const monthLabel = (d) => new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(d);
const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const STATUS_ORDER = { overdue: 0, due_soon: 1, upcoming: 2, in_progress: 3, completed: 4, no_action_needed: 5 };

function chipClass(kind, status) {
  if (status === 'overdue') return 'bg-red-50 text-red-800 border-red-200 hover:bg-red-100';
  if (status === 'completed' || status === 'no_action_needed') return 'bg-slate-50 text-slate-400 border-slate-200 line-through';
  return KIND_META[kind].chip;
}

/** Month grid for one program. */
function MonthGrid({ items, monthDate, parseDateOnly, getEffectiveStatus, onSelect, emptyText }) {
  const [expandedDay, setExpandedDay] = useState(null);

  const byDay = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      const raw = item.due_date || item.end_date || item.start_date;
      const d = raw ? parseDateOnly(raw) : null;
      if (!d) return;
      const key = dayKey(d);
      if (!map[key]) map[key] = [];
      map[key].push(item);
    });
    Object.values(map).forEach((list) => list.sort(
      (a, b) => (STATUS_ORDER[getEffectiveStatus(a)] ?? 2) - (STATUS_ORDER[getEffectiveStatus(b)] ?? 2)
    ));
    return map;
  }, [items, parseDateOnly, getEffectiveStatus]);

  const cells = useMemo(() => {
    const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const start = new Date(first);
    start.setDate(start.getDate() - start.getDay()); // back to Sunday
    const out = [];
    const cursor = new Date(start);
    for (let i = 0; i < 42; i += 1) {
      out.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    // Drop a trailing all-out-of-month week so short months stay compact.
    const lastRow = out.slice(35);
    if (lastRow.every((d) => d.getMonth() !== monthDate.getMonth())) return out.slice(0, 35);
    return out;
  }, [monthDate]);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const monthCount = useMemo(
    () => cells.filter((d) => d.getMonth() === monthDate.getMonth()).reduce((n, d) => n + (byDay[dayKey(d)]?.length || 0), 0),
    [cells, byDay, monthDate]
  );
  const expandedItems = expandedDay ? (byDay[expandedDay] || []) : [];

  return (
    <>
      <div className="grid grid-cols-7 border-t border-slate-100 text-[11px] font-medium uppercase tracking-wide text-slate-400">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="px-2 py-1.5 text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 border-t border-slate-100 flex-1">
        {cells.map((d) => {
          const key = dayKey(d);
          const inMonth = d.getMonth() === monthDate.getMonth();
          const isToday = d.getTime() === today.getTime();
          const dayItems = byDay[key] || [];
          const shown = dayItems.slice(0, 3);
          const extra = dayItems.length - shown.length;
          return (
            <div key={key} className={`min-h-[84px] border-b border-r border-slate-100 p-1 ${inMonth ? 'bg-white' : 'bg-slate-50/60'}`}>
              <div className="flex justify-end">
                <span className={`text-[11px] leading-5 w-5 h-5 text-center rounded-full ${
                  isToday ? 'bg-slate-900 text-white font-semibold' : inMonth ? 'text-slate-500' : 'text-slate-300'
                }`}>
                  {d.getDate()}
                </span>
              </div>
              <div className="space-y-0.5 mt-0.5">
                {shown.map((item) => {
                  const kind = itemKind(item);
                  const status = getEffectiveStatus(item);
                  return (
                    <button
                      key={item.id || `${item.title}-${key}`}
                      type="button"
                      onClick={() => onSelect(item)}
                      title={`${KIND_META[kind].label} — ${item.title}`}
                      className={`w-full text-left truncate rounded-md border px-1.5 py-0.5 text-[11px] font-medium transition ${chipClass(kind, status)}`}
                    >
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${status === 'overdue' ? 'bg-red-500' : KIND_META[kind].dot}`} />
                      <span className="align-middle">{item.title}</span>
                    </button>
                  );
                })}
                {extra > 0 && (
                  <button
                    type="button"
                    onClick={() => setExpandedDay((prev) => (prev === key ? null : key))}
                    className="w-full text-left rounded-md px-1.5 py-0.5 text-[11px] font-medium text-slate-500 hover:bg-slate-100"
                  >
                    {expandedDay === key ? 'Show less' : `+${extra} more`}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {expandedItems.length > 0 && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-3 py-2 space-y-1">
          {expandedItems.map((item) => {
            const kind = itemKind(item);
            const status = getEffectiveStatus(item);
            return (
              <button
                key={`exp-${item.id || item.title}`}
                type="button"
                onClick={() => onSelect(item)}
                className={`w-full text-left truncate rounded-md border px-2 py-1 text-xs font-medium ${chipClass(kind, status)}`}
              >
                <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ${status === 'overdue' ? 'bg-red-500' : KIND_META[kind].dot}`} />
                <span className="align-middle">{item.title}</span>
                {item.assignee_name || item.assignee_email ? (
                  <span className="ml-1.5 text-slate-400 font-normal align-middle">· {item.assignee_name || item.assignee_email}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
      {monthCount === 0 && (
        <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-400">{emptyText}</div>
      )}
    </>
  );
}

/** List for one program, month-scoped, grouped by date. Asana-like rows. */
function ProgramList({ items, monthDate, parseDateOnly, getEffectiveStatus, onSelect, emptyText }) {
  const groups = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      const raw = item.due_date || item.end_date || item.start_date;
      const d = raw ? parseDateOnly(raw) : null;
      if (!d || d.getMonth() !== monthDate.getMonth() || d.getFullYear() !== monthDate.getFullYear()) return;
      const key = dayKey(d);
      if (!map[key]) map[key] = { date: d, items: [] };
      map[key].items.push(item);
    });
    Object.values(map).forEach((g) => g.items.sort(
      (a, b) => (STATUS_ORDER[getEffectiveStatus(a)] ?? 2) - (STATUS_ORDER[getEffectiveStatus(b)] ?? 2)
    ));
    return Object.keys(map).sort().map((k) => map[k]);
  }, [items, monthDate, parseDateOnly, getEffectiveStatus]);

  if (groups.length === 0) {
    return <div className="px-4 py-6 border-t border-slate-100 text-sm text-slate-400">{emptyText}</div>;
  }
  return (
    <div className="border-t border-slate-100 divide-y divide-slate-100">
      {groups.map((g) => (
        <div key={dayKey(g.date)}>
          <div className="px-4 pt-3 pb-1 flex items-baseline gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(g.date)}
            </span>
            <span className="text-[11px] text-slate-400">{g.items.length} item{g.items.length === 1 ? '' : 's'}</span>
          </div>
          {g.items.map((item) => {
            const kind = itemKind(item);
            const status = getEffectiveStatus(item);
            return (
              <button
                key={`li-${item.id || item.title}`}
                type="button"
                onClick={() => onSelect(item)}
                className="w-full text-left px-4 py-2 flex items-center gap-3 hover:bg-slate-50 transition"
              >
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${status === 'overdue' ? 'bg-red-500' : KIND_META[kind].dot}`} />
                <span className={`flex-1 min-w-0 truncate text-sm ${status === 'completed' ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                  {item.title}
                </span>
                <span className="hidden sm:block text-xs text-slate-400 truncate max-w-[10rem]">
                  {item.assignee_name || item.assignee_email || ''}
                </span>
                <span className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-lg border text-[11px] font-medium ${STATUS_PILL[status] || STATUS_PILL.upcoming}`}>
                  {STATUS_LABEL[status] || 'Upcoming'}
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/**
 * "When it happens" — the contract's event-conditioned duties, visible
 * BEFORE any event fires. Amber, matching the triggered-duty chips these
 * become once reported.
 */
function TriggerablePanel({ duties, loaded }) {
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  if (!loaded || duties.length === 0) return null;
  return (
    <div className="border-t border-amber-200/70 bg-amber-50/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-2.5 flex items-center gap-2 text-left hover:bg-amber-50 transition"
      >
        <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
        <span className="text-sm font-semibold text-amber-900 flex-1">
          When it happens — duties an event can trigger ({duties.length})
        </span>
        <svg className={`w-4 h-4 text-amber-700 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-1 max-h-96 overflow-y-auto">
          <p className="px-1 pb-1 text-[11px] text-amber-800/80">
            No dates and no overdue until the event occurs. Report the event in State Contracts and the dated duty lands on this calendar in amber.
          </p>
          {duties.map((d) => (
            <div key={d.id} className="bg-white border border-amber-200/70 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setExpandedId((prev) => (prev === d.id ? null : d.id))}
                className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-amber-50/60 transition"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                <span className="flex-1 min-w-0 truncate text-sm text-slate-800">{d.title}</span>
                {(d.owner_name || d.owner_email || d.department_name) && (
                  <span className="hidden sm:block text-xs text-slate-400 truncate max-w-[9rem]">
                    {d.owner_name || d.owner_email || `All in ${d.department_name}`}
                  </span>
                )}
              </button>
              {expandedId === d.id && (
                <div className="px-3 pb-2.5 pt-0.5 text-xs space-y-1 border-t border-amber-100">
                  {d.trigger_text && (
                    <p className="text-slate-600"><span className="font-semibold text-amber-800">Trigger:</span> {d.trigger_text}</p>
                  )}
                  {d.sla_text && (
                    <p className="text-slate-600"><span className="font-semibold text-amber-800">Then:</span> {d.sla_text}</p>
                  )}
                  {!d.trigger_text && !d.sla_text && d.obligation_text && (
                    <p className="text-slate-600">{d.obligation_text}</p>
                  )}
                  {d.citation && <p className="text-slate-400">{d.citation}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProgramPanel({ title, accentBar, legend, children }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden flex flex-col">
      <div className={`h-1 ${accentBar}`} />
      <div className="px-4 pt-3 pb-2 flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        <div className="flex items-center gap-3 flex-wrap">
          {legend.map((k) => (
            <span key={k} className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
              <span className={`w-2 h-2 rounded-full ${KIND_META[k].dot}`} />
              {KIND_META[k].label}
            </span>
          ))}
        </div>
      </div>
      {children}
    </section>
  );
}

export default function DualProgramCalendar({ items, parseDateOnly, getEffectiveStatus, onSelect }) {
  const [monthDate, setMonthDate] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [displayMode, setDisplayMode] = useState('month'); // 'month' | 'list'
  const [triggerable, setTriggerable] = useState([]);
  const [triggerableLoaded, setTriggerableLoaded] = useState(false);
  const apiBaseUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    let isMounted = true;
    fetch(`${apiBaseUrl}/api/calendar/triggerable`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (isMounted) { setTriggerable(Array.isArray(data) ? data : []); setTriggerableLoaded(true); }
      })
      .catch(() => { if (isMounted) setTriggerableLoaded(true); });
    return () => { isMounted = false; };
  }, [apiBaseUrl]);

  const medicare = useMemo(() => items.filter((i) => itemKind(i) === 'medicare'), [items]);
  const medicaid = useMemo(() => items.filter((i) => itemKind(i) !== 'medicare'), [items]);

  const shift = (delta) => setMonthDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  const Body = displayMode === 'month' ? MonthGrid : ProgramList;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => shift(-1)} aria-label="Previous month"
            className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 flex items-center justify-center">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <button type="button" onClick={() => shift(1)} aria-label="Next month"
            className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 flex items-center justify-center">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>
          <h2 className="text-lg font-semibold text-slate-800 ml-2">{monthLabel(monthDate)}</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg bg-slate-100 p-0.5 border border-slate-200" role="tablist" aria-label="Display mode">
            {[{ id: 'month', label: 'Month' }, { id: 'list', label: 'List' }].map((m) => (
              <button
                key={m.id}
                type="button"
                role="tab"
                aria-selected={displayMode === m.id}
                onClick={() => setDisplayMode(m.id)}
                className={`px-3 py-1 rounded-md text-sm font-medium transition ${
                  displayMode === m.id ? 'bg-white text-slate-800 shadow-sm border border-slate-200' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => { const d = new Date(); setMonthDate(new Date(d.getFullYear(), d.getMonth(), 1)); }}
            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Today
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        <ProgramPanel title="Medicare" accentBar="bg-blue-500" legend={['medicare']}>
          <Body
            items={medicare}
            monthDate={monthDate}
            parseDateOnly={parseDateOnly}
            getEffectiveStatus={getEffectiveStatus}
            onSelect={onSelect}
            emptyText="Nothing due this month for Medicare."
          />
        </ProgramPanel>
        <ProgramPanel title="Medicaid — state contract" accentBar="bg-emerald-500" legend={['deliverable', 'triggered']}>
          <Body
            items={medicaid}
            monthDate={monthDate}
            parseDateOnly={parseDateOnly}
            getEffectiveStatus={getEffectiveStatus}
            onSelect={onSelect}
            emptyText="Nothing due this month for the state contract."
          />
          <TriggerablePanel duties={triggerable} loaded={triggerableLoaded} />
        </ProgramPanel>
      </div>
    </div>
  );
}
